
import React from 'react';
import { motion } from 'framer-motion';
import { Bell, Search, Table, Settings } from 'lucide-react';

const StepIcon = ({ icon: Icon, color, delay, number }: { icon: any, color: string, delay: number, number: number }) => (
  <div className="relative">
    <motion.div
      initial={{ rotate: -10, y: 0 }}
      animate={{ 
        rotate: [10, -10, 10],
        y: [0, -5, 0]
      }}
      transition={{ 
        duration: 4, 
        repeat: Infinity, 
        ease: "easeInOut",
        delay: delay 
      }}
      className={`w-20 h-20 rounded-2xl bg-white shadow-soft border border-brand-offwhite flex items-center justify-center ${color} relative z-10`}
    >
      <Icon className="w-10 h-10" />
    </motion.div>
    {/* Number Badge */}
    <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-brand-blue text-white text-base font-bold flex items-center justify-center z-20 shadow-lg border-2 border-white">
      {number}
    </div>
  </div>
);

const MovingGears = () => (
  <div className="hidden md:flex absolute top-1/2 left-full w-[calc(100vw/3-160px)] max-w-[140px] h-12 -translate-y-1/2 items-center justify-center gap-1 z-0 ml-2">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      className="text-brand-blue/10"
    >
      <Settings className="w-10 h-10" />
    </motion.div>
    <motion.div
      animate={{ rotate: -360 }}
      transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
      className="text-brand-blue/10 -mt-4"
    >
      <Settings className="w-6 h-6" />
    </motion.div>
  </div>
);

const HowItWorks = () => {
  const steps = [
    {
      title: "Just add your jobs",
      description: "Connect your ATS.",
      icon: Bell,
      color: "text-amber-500",
      delay: 0
    },
    {
      title: "Specify your key criteria for the role",
      description: "Your must-haves and preferences are carefully noted",
      icon: Search,
      color: "text-brand-blue",
      delay: 0.5
    },
    {
      title: "Top candidates found and sent to you",
      description: "Candidates are sourced and the ones who match your needs are sent to your ATS",
      icon: Table,
      color: "text-emerald-500",
      delay: 1
    }
  ];

  return (
    <section className="section-padding bg-[#FAFBFF] relative overflow-hidden">
      <div className="container-custom">
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-brand-darkgrey mb-4">
              No need to learn a new tool
            </h2>
            <h2 className="text-xl md:text-2xl text-brand-grey font-medium max-w-3xl mx-auto mb-6">
              Zero process change. We send you top candidates right where your team works.
            </h2>
          </motion.div>
          <div className="h-1.5 w-20 bg-brand-yellow mx-auto rounded-full" />
        </div>

        <div className="flex flex-col md:flex-row items-start justify-between gap-16 md:gap-4 relative">
          {steps.map((step, index) => (
            <div key={index} className="flex-1 flex flex-col items-center text-center">
              <div className="mb-10 relative">
                <StepIcon 
                  icon={step.icon} 
                  color={step.color} 
                  delay={step.delay} 
                  number={index + 1} 
                />
                {index < steps.length - 1 && <MovingGears />}
              </div>
              
              <div className="flex flex-col items-center">
                <h3 className="text-xl font-bold text-brand-darkgrey mb-4 h-auto md:h-14 flex items-center justify-center">
                  {step.title}
                </h3>
                <p className="text-brand-grey max-w-[280px] leading-relaxed min-h-[60px]">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-16"
        >
          <p className="text-lg md:text-xl text-brand-darkgrey font-semibold underline">
            Your team also has full access to the Cutshort platform
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
